import moment from 'moment';
import { notificationError, notificationSuccess } from '../../../../../core/actions';
import { clientbookApi } from '../../../../../core/clientbook-api';
import store from '../../../../../core/store';
import { setMsgChatMessages, setInboxList, setStoreInboxList } from '../../../../../reducers/messaging';
import { actionTextAreaFileActions } from '../../../TextAreaWithActions/useActionTextAreaFiles';
import { getValidatedStoreId } from '../../../../../utils/storeAccess';
import { getUploadErrorMessage } from '../../../../../utils/uploadErrors';

const containsSubstring = substr => str => str.includes(substr);

const sendByType = ({ type, chatId, clientId, message, imageUrls, blobImages, blobVideo, storeId, isOpen }) => {
  if (type === 'G') {
    return clientbookApi.post({
      url: `/google/reviews/${chatId}/reply`,
      data: { replyComment: message },
    });
  }

  if (type === 'F' || type === 'I') {
    const platform = type === 'F' ? 'facebook' : 'instagram';
    const hasFiles = blobImages.length > 0 || blobVideo;
    if (hasFiles) {
      const formData = new FormData();
      if (message) formData.append('message', message);
      if (isOpen !== undefined) formData.append('isOpen', isOpen);
      blobImages.forEach(file => formData.append('images', file));
      if (blobVideo) formData.append('video', blobVideo);
      return clientbookApi.postFormData({ url: `/meta/${platform}/chats/${chatId}/messages`, formData });
    }
    return clientbookApi.post({
      url: `/meta/${platform}/chats/${chatId}/messages`,
      data: {
        ...(message && { message }),
        ...(isOpen !== undefined && { isOpen }),
      },
    });
  }

  // SMS (type === 'C' or default)
  const hasFiles = blobImages.length > 0 || blobVideo;
  if (hasFiles) {
    const formData = new FormData();
    if (message) formData.append('message', message);
    imageUrls.forEach(url => formData.append('imageUrls[]', url));
    if (storeId) formData.append('storeId', String(storeId));
    if (isOpen !== undefined) formData.append('isOpen', isOpen);
    blobImages.forEach(file => formData.append('images', file));
    if (blobVideo) formData.append('video', blobVideo);
    return clientbookApi.postFormData({ url: `/chats/${clientId}`, formData });
  }
  return clientbookApi.post({
    url: `/chats/${clientId}`,
    data: {
      ...(message && { message }),
      ...(imageUrls.length > 0 && { imageUrls }),
      ...(storeId && { storeId: String(storeId) }),
      ...(isOpen !== undefined && { isOpen }),
    },
  });
};

export default async ({
  messageTxt,
  fileData,
  chatId,
  clientId,
  timeOffset,
  setMessageTxt,
  fileDispatch,
  archive,
  type,
}) => {
  const props = store.getState();
  const { merchantId, selectedStore, stores } = props.auth;
  const { sendStoreId } = props.messaging.client;

  if (!messageTxt.length && !fileData.images.urls.length && !fileData?.videos?.urls?.length) {
    notificationError('Please Type A Message');
    return;
  }

  if (!props.messaging.client.chat_id || !merchantId) {
    return;
  }

  const validatedStoreId = getValidatedStoreId(sendStoreId, selectedStore, stores, {
    messageType: type || 'SMS',
    source: 'sendChatMessage',
  });

  const message = messageTxt.replace('[Review Link]', ':review:');
  const imageUrls = fileData.images.urls.filter(containsSubstring('s3.amazonaws.com'));
  const blobImages = fileData.images.files;
  const blobVideo = fileData.videos.files?.[0] ?? null;
  const isOpen = archive ? 'false' : undefined;

  setMessageTxt('');

  let respData;
  try {
    respData = await sendByType({
      type,
      chatId,
      clientId,
      message,
      imageUrls,
      blobImages,
      blobVideo,
      storeId: validatedStoreId,
      isOpen,
    });
  } catch (error) {
    notificationError(
      getUploadErrorMessage(error, blobImages.length > 0 || Boolean(blobVideo)) || 'Failed To Send Message',
    );
    return;
  }

  notificationSuccess(archive ? 'Conversation Closed' : 'Message Successfully Sent');

  const tempChatList = props.messaging.chatMessages;
  const cutFilteredConvo = tempChatList.slice(0, 1);
  let shouldWeShift = true;
  cutFilteredConvo.forEach(conversation => {
    if (conversation.user_id === props.auth.userId && conversation.msg === message && !fileData.videos.urls.length) {
      shouldWeShift = false;
    }
  });

  if (archive) {
    setInboxList(props.messaging.inboxList.filter(item => item.id !== chatId));
    setStoreInboxList(props.messaging.storeInboxList.filter(item => item.id !== chatId));
  } else if (shouldWeShift) {
    const fakeChatObj = {
      client_id: props.messaging.client.client_id,
      id: `FAKE${props.messaging.client.client_id}${Math.floor(Math.random() * 9999)}`,
      chatId,
      imageUrl: null,
      collectionId: null,
      collectionName: null,
      message,
      productId: null,
      productImage: null,
      productName: null,
      productPrice: null,
      sent: +moment().subtract(timeOffset, 'minutes').format('YYYYMMDDHHmmss'),
      sentTimestamp: moment(),
      storeName: null,
      thumbnailUrl: null,
      userId: props.auth.userId,
      userName: `${props.auth.firstName || ''} ${props.auth.lastName || ''}`,
      userImage: `${props.auth.image || ''}`,
      videoUrl: null,
      uuid: respData?.uuid,
    };
    fakeChatObj.storeId = validatedStoreId;

    if (messageTxt) {
      tempChatList.unshift(fakeChatObj);
    }

    if (fileData?.images?.urls?.length > 0) {
      fileData.images.urls.forEach(url => {
        tempChatList.unshift({ ...fakeChatObj, imageUrl: url, fakeImage: true, message: null });
      });
    } else if (fileData.videos.urls.length > 0) {
      fileData.videos.urls.forEach((url, index) => {
        tempChatList.unshift({
          ...fakeChatObj,
          videoUrl: url,
          thumbnailUrl: fileData.videos.videoFrames.urls[index],
          fakeVideo: true,
        });
      });
    }

    setMsgChatMessages([...tempChatList]);
  }

  fileDispatch({ type: actionTextAreaFileActions.clear });
};
